import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import React, { useEffect } from 'react';
import { DEFAULT_IMAGES } from '../../../../../../corelogic/domain/constants/images.constants';
import { Gender } from '../../../../../../corelogic/domain/models/gender.enum';
import { getDefaultFighterImage } from '../../../../../../corelogic/domain/utils/fighterImage.utils';
import { isValidGender } from '../../../../../../corelogic/domain/utils/gender.utils';
import { useFighterImageState } from '../../../hooks/useFighterImage';
import { FighterImageService } from '../../../services/FighterImage.service';
import './SelectorCard.css';

const SelectorCard: React.FC<{
  cardId: number;
  name: string;
  opponent: string;
  fightDate: string;
  points: number | null;
  selectedCardId: number | null;
  title: string;
  onCardClick: () => void;
  isLoading?: boolean;
  firstname: string;
  lastname: string;
  dob: Date | null;
  gender: Gender | unknown;
}> = ({
  cardId,
  name,
  opponent,
  fightDate,
  points,
  selectedCardId,
  onCardClick,
  title,
  isLoading = false,
  firstname,
  lastname,
  dob,
  gender,
}) => {
  // Check if fight is completed (has points and points are not 0)
  const isFightCompleted =
    (points !== null && points !== 0) || new Date(fightDate) < new Date();

  // Check if we have valid fighter data for image retrieval
  const hasValidFighterData =
    dob && ((firstname && firstname !== '') || (lastname && lastname !== ''));

  // Use the fighter image hook only for valid fighter data
  const { fighterImage, isLoading: isImageLoading } = useFighterImageState(
    hasValidFighterData ? firstname : undefined,
    hasValidFighterData ? lastname : undefined,
    hasValidFighterData ? dob : undefined,
  );

  // Request fighter image if needed
  useEffect(() => {
    if (hasValidFighterData && isValidGender(gender)) {
      FighterImageService.retrieveFighterImage(
        firstname,
        lastname,
        dob,
        gender,
      );
    }
  }, [firstname, lastname, dob, gender, hasValidFighterData]);

  // Check if fight is happening today (compare date only, not time)
  const isFightHappeningToday = (() => {
    const fightDateObj = new Date(fightDate);
    const today = new Date();
    const fightDateOnly = new Date(
      fightDateObj.getFullYear(),
      fightDateObj.getMonth(),
      fightDateObj.getDate(),
    );
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );

    return (
      fightDateOnly.getTime() === todayOnly.getTime() &&
      (points === null || points === 0)
    );
  })();

  const handleCardClick = () => {
    if (!isFightCompleted && !isFightHappeningToday) {
      onCardClick();
    }
  };

  // Get the image to display - use gender-specific default if available, even without full fighter data
  const displayImage =
    fighterImage ||
    (isValidGender(gender)
      ? getDefaultFighterImage(gender)
      : DEFAULT_IMAGES.FIGHTER_GENERIC);

  return (
    <Card
      onClick={handleCardClick}
      className="selector-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        height: '100%',
        borderRadius: '12px',
        backgroundColor: 'rgba(20, 20, 20, 0.9)',
        cursor:
          isFightCompleted || isFightHappeningToday ? 'not-allowed' : 'pointer',
        opacity:
          isFightCompleted || isFightHappeningToday
            ? 0.7
            : selectedCardId === null || selectedCardId === cardId
              ? 1
              : 0.6,
        border:
          selectedCardId === cardId
            ? '3px solid white'
            : '1px solid rgba(255, 255, 255, 0.3)',
        transition: 'all 0.3s ease',
        '&:hover': {
          opacity: isFightCompleted || isFightHappeningToday ? 0.7 : 1,
          border:
            isFightCompleted || isFightHappeningToday
              ? '1px solid rgba(255, 255, 255, 0.3)'
              : selectedCardId === cardId
                ? '3px solid white'
                : '1px solid white',
        },
        position: 'relative',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '35%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: { xs: '8px', sm: '15px' },
          marginBottom: { xs: '0px', sm: '1px' },
        }}
      >
        {isLoading || isImageLoading ? (
          <CircularProgress
            size={30}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          />
        ) : (
          <Box
            component="img"
            sx={{
              maxWidth: { xs: '96%', sm: '98%' },
              maxHeight: { xs: '96%', sm: '98%' },
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '3px',
            }}
            src={displayImage}
            alt="Fighter Image"
          />
        )}
      </Box>
      <CardContent
        sx={{ textAlign: 'center', padding: { xs: '2px', sm: '4px' } }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            fontFamily: 'Rubik Mono One',
            color: 'white',
            fontSize: {
              xs: 'clamp(0.7rem, 2.5vw, 1.1rem)',
              sm: 'clamp(0.4rem, 1vw, 2.0rem)',
            },
            textTransform: 'uppercase',
            mb: { xs: '2px', sm: 'initial' },
          }}
        >
          {name}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'Bebas Neue',
            color: '#cccccc',
            fontSize: {
              xs: 'clamp(0.6rem, 2.2vw, 1rem)',
              sm: 'clamp(0.35rem, 0.8vw, 2rem)',
            },
            display: 'block',
            mb: { xs: '1px', sm: 'initial' },
          }}
        >
          {opponent ? `vs. ${opponent}` : '-'}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            fontFamily: 'Bebas Neue',
            color: '#cccccc',
            fontSize: {
              xs: 'clamp(0.6rem, 2.2vw, 1rem)',
              sm: 'clamp(0.35rem, 0.8vw, 2rem)',
            },
            display: 'block',
          }}
        >
          {fightDate ? fightDate : '-'}
        </Typography>
      </CardContent>

      <Box
        sx={{
          width: '100%',
          backgroundColor:
            isFightCompleted || isFightHappeningToday
              ? 'rgba(60, 60, 60, 0.7)' // Gray when fight is completed
              : selectedCardId === cardId
                ? 'rgba(139, 0, 0, 0.7)' // Dark red when selected
                : 'rgba(0, 0, 0, 0.7)', // Original black when not selected
          borderRadius: '0 0 12px 12px',
          textAlign: 'center',
          padding: '8px 0',
          transition: 'background-color 0.3s ease', // Smooth transition for color change
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontFamily: 'Bebas Neue',
            color:
              isFightCompleted || isFightHappeningToday
                ? 'rgba(255, 255, 255, 0.5)'
                : 'rgba(255, 255, 255, 0.7)',
            fontSize: { xs: '0.8rem', sm: 'clamp(0.5rem, 1.2vw, 1.5rem)' },
            transition: 'color 0.3s ease',
            '.selector-card:hover &, .selector-card.selected &': {
              color:
                isFightCompleted || isFightHappeningToday
                  ? 'rgba(255, 255, 255, 0.5)'
                  : '#ffffff',
            },
          }}
        >
          {isFightHappeningToday
            ? 'Fight In Progress!'
            : isFightCompleted
              ? 'Fight Completed'
              : selectedCardId === cardId
                ? 'Cancel'
                : `Select Fighter ${cardId}`}
        </Typography>
      </Box>

      {/* Title Crown Box */}
      {title === 'T' && (
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            left: '4px',
            padding: '2px 4px',
            borderRadius: '8px',
            border: '1px solid gold',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(0.3rem, 0.7vw, 1rem)',
              color: 'gold',
            }}
          >
            👑
          </Typography>
        </Box>
      )}

      {/* Points Box */}
      {name !== 'No Fighter Selected' && (
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            padding: '2px 4px',
            borderRadius: '8px',
            border: `1px solid ${points !== null && points !== 0 ? '#8b0000' : 'rgba(255, 255, 255, 0.3)'}`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          }}
        >
          <Typography
            sx={{
              fontSize: 'clamp(0.3rem, 0.7vw, 1rem)',
              fontFamily: 'Rubik Mono One',
              color:
                points !== null && points !== 0
                  ? '#ffffff'
                  : 'rgba(255, 255, 255, 0.5)',
            }}
          >
            {points !== null && points !== 0 ? points : 'N/A'}
          </Typography>
        </Box>
      )}
    </Card>
  );
};

export default SelectorCard;
